// "Make this tool work offline": the runtime and a model, stored on request.
//
// Without this, a model tool works offline only on a page where it has already
// run, because the runtime lives in that page's memory. With it, the four
// files a model tool needs are kept in Cache Storage and a small service worker
// (src/assets/sw/tools-sw.js) answers for them, so a page opened later works
// with no connection at all. It is a visible, opt-in download with its size on
// the button, not something that happens behind the visitor's back.

import { formatBytes } from './images.js';

const CACHE = 'imageserver-tools-models-v1'; // shared with tools/onnx.js and the worker
const WORKER_URL = '/tools/sw.js';

// What the runtime is made of. Sizes are what the button quotes before the
// download starts; the download itself counts real bytes.
const RUNTIME_FILES = [
    { url: '/plugins/onnxruntime/ort.wasm.min.js', type: 'text/javascript', bytes: 46627 },
    { url: '/plugins/onnxruntime/ort-wasm-simd-threaded.mjs', type: 'text/javascript', bytes: 26583 },
    { url: '/plugins/onnxruntime/ort-wasm-simd-threaded.wasm', type: 'application/wasm', bytes: 12666427 },
];

/** Whether this browser can do it at all: a service worker needs HTTPS. */
export function offlineSupported() {
    return Boolean(window.isSecureContext && 'serviceWorker' in navigator && 'caches' in window);
}

function filesFor(model) {
    return [...RUNTIME_FILES, { url: model.file, type: 'application/octet-stream', bytes: model.bytes }];
}

/** Whether everything this model needs is already stored. */
export async function isReadyOffline(model) {
    if (!offlineSupported()) return false;
    try {
        const cache = await caches.open(CACHE);
        const hits = await Promise.all(filesFor(model).map(file => cache.match(file.url)));
        const registered = await navigator.serviceWorker.getRegistration('/tools/');
        return hits.every(Boolean) && Boolean(registered);
    } catch (err) {
        return false;
    }
}

/** Download and store what is missing, reporting 0 to 1 across all of it. */
async function prepare(model, onProgress) {
    await navigator.serviceWorker.register(WORKER_URL, { scope: '/tools/' });
    const cache = await caches.open(CACHE);

    const files = filesFor(model);
    const missing = [];
    for (const file of files) {
        if (!(await cache.match(file.url))) missing.push(file);
    }
    const total = missing.reduce((sum, file) => sum + file.bytes, 0) || 1;
    let done = 0;

    for (const file of missing) {
        const response = await fetch(file.url, { cache: 'no-store' });
        if (!response.ok || !response.body) throw new Error(`Could not download ${file.url}`);
        const reader = response.body.getReader();
        const chunks = [];
        for (;;) {
            const { done: finished, value } = await reader.read();
            if (finished) break;
            chunks.push(value);
            done += value.length;
            if (onProgress) onProgress(Math.min(1, done / total));
        }
        // Stored with its type: the runtime compiles the WebAssembly as a
        // stream and imports the glue module, and both refuse a response
        // without the right one.
        await cache.put(file.url, new Response(new Blob(chunks, { type: file.type }), {
            headers: { 'Content-Type': file.type },
        }));
    }
    await navigator.serviceWorker.ready;
}

/**
 * Wire the offline button of a model tool.
 *
 * @param {object}   options
 * @param {Element}  options.root    Wrapper, rendered hidden; shown where supported.
 * @param {Element}  options.button  The button.
 * @param {Element}  options.note    Line under it, a polite live region.
 * @param {Function} options.model   Returns the model the tool is set to now.
 * @returns {Function} Call when the model changes, to re-check and relabel.
 */
export function wireOffline({ root, button, note, model }) {
    if (!root || !offlineSupported()) return () => {};
    root.hidden = false;
    const label = button.querySelector('[data-offline-label]');
    let busy = false;

    const sizeOf = m => filesFor(m).reduce((sum, file) => sum + file.bytes, 0);

    const refresh = async () => {
        if (busy) return;
        const current = model();
        if (await isReadyOffline(current)) {
            button.hidden = true;
            note.textContent = `Ready offline. This tool now works in this browser without a connection.`;
        } else {
            button.hidden = false;
            button.disabled = false;
            label.textContent = `Make this tool work offline (${formatBytes(sizeOf(current))})`;
            const which = current.label ? `the ${current.label} model` : 'the model';
            note.textContent = `Stores the runtime and ${which} in this browser, so a page opened later works without a connection.`;
        }
    };

    button.addEventListener('click', async () => {
        if (busy) return;
        busy = true;
        button.disabled = true;
        const current = model();
        try {
            await prepare(current, fraction => {
                label.textContent = `Downloading, ${Math.round(fraction * 100)}%`;
            });
            busy = false;
            await refresh();
        } catch (err) {
            busy = false;
            button.disabled = false;
            label.textContent = 'Try again';
            note.textContent = navigator.onLine === false
                ? 'You are offline. Connect, then try again.'
                : 'The download did not finish. Try again.';
        }
    });

    refresh();
    return refresh;
}
